import React from "react";
import posthog from "posthog-js";
import { PostHogProvider, usePostHog } from "posthog-js/react";
import { useLocation } from "react-router";
import { useAuth } from "./Auth";
import { getPosthogConfig } from "./posthogConfig";

// The current URL's campaign params, null when missing, as PostHog itself records them.
function urlCampaign() {
  const params = new URLSearchParams(window.location.search);
  return Object.fromEntries(
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].map((key) => [
      key,
      params.get(key),
    ]),
  );
}

// Initialized before the first render, so PostHog reads a link's campaign params before the app
// redirects away from them.
const config = getPosthogConfig();
if (config) {
  posthog.init(config.key, {
    api_host: config.host,
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: false,
    debug: import.meta.env.DEV,
    session_recording: {
      recordCrossOriginIframes: true,
    },
    loaded: (client) => {
      client.register({ ENVIRONMENT: import.meta.env.MODE });
      // Campaign params last for one session. A reset (noSessionId) keeps the ones set after it.
      client.onSessionId((_sessionId, _windowId, changeReason) => {
        if (changeReason && !changeReason.noSessionId) {
          client.register_for_session(urlCampaign());
        }
      });
    },
  });
}

export function WithPostHog({ children }: React.PropsWithChildren) {
  if (!config) {
    return children;
  }
  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}

// Whether a user signed in during this page load. It lives outside the component, which remounts
// when logging out clears the query cache.
let hasIdentified = false;

export function PostHogInstrumentation(props: React.PropsWithChildren) {
  const posthog = usePostHog();
  const auth = useAuth();
  const location = useLocation();

  React.useEffect(() => {
    if (!auth.isHydrated) {
      return;
    }
    if (!auth.isAuthenticated) {
      if (hasIdentified) {
        // Signing out leaves the last screen.
        posthog.capture("screenleave", {
          url: window.location.pathname,
        });
      }
      // PostHog keeps only verified identities, but a visitor keeps the campaign params of the link
      // they arrived with.
      posthog?.reset();
      posthog?.register_for_session(urlCampaign());
      hasIdentified = false;
      return;
    }
    const user = auth.user;
    posthog?.identify(
      user.id,
      {
        emails: user.identities.map((i) => i.email),
        is_root: user.is_root,
        name: user.name,
      },
      {
        organization: {
          id: user.organization_id,
          name: user.organization.name,
        },
      },
    );

    posthog?.group("organization", user.organization.id, { name: user.organization.name });
    hasIdentified = true;
  }, [posthog, auth.isHydrated, auth.isAuthenticated, auth.user]);

  React.useEffect(() => {
    if (!hasIdentified) {
      return;
    }
    const pathname = location.pathname;
    posthog.capture("screenview", {
      url: pathname,
    });
    return () => {
      posthog.capture("screenleave", {
        url: pathname,
      });
    };
  }, [posthog, location.pathname, auth.isAuthenticated]);

  return props.children;
}
